import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ScanFace, Brain } from 'lucide-react';
import { Link } from 'react-router-dom';

const SCAN_STATES = ['INITIALIZING', 'SCANNING', 'ANALYZING', 'VERIFIED'];

/* Photo-based biometric scanner panel */
const FaceScanner = () => {
  const [scanStep, setScanStep] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setScanStep(s => (s + 1) % SCAN_STATES.length), 2200);
    return () => clearInterval(t);
  }, []);

  const isVerified = scanStep === 3;
  const accentColor  = isVerified ? '#84CC16' : '#9333EA';
  const accentColor2 = isVerified ? '#84CC16' : '#EC4899';

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      maxWidth: '420px',
      borderRadius: '16px',
      overflow: 'hidden',
      border: `1px solid ${isVerified ? 'rgba(132,204,22,0.4)' : 'rgba(147,51,234,0.35)'}`,
      boxShadow: isVerified
        ? '0 30px 80px rgba(132,204,22,0.15)'
        : '0 30px 80px rgba(147,51,234,0.2)',
      transition: 'all 0.6s ease',
      background: '#08050F',
    }}>

      {/* ── Top HUD bar ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '10px 16px',
        background: 'rgba(8,5,15,0.9)',
        borderBottom: '1px solid rgba(147,51,234,0.12)',
        position: 'relative', zIndex: 10,
      }}>
        <span style={{ fontFamily: "'Orbitron',monospace", fontSize: 9, letterSpacing: 3, color: '#4A3D6B', textTransform: 'uppercase' }}>
          WHOOPPE BIOMETRIC v2.4
        </span>
        <span style={{ display:'flex', alignItems:'center', gap:6, fontFamily:"'Orbitron',monospace", fontSize:9, letterSpacing:2, color: accentColor, textTransform:'uppercase' }}>
          <span style={{ width:6, height:6, borderRadius:'50%', background: accentColor, boxShadow:`0 0 8px ${accentColor}`, animation:'blink 1.2s ease-in-out infinite' }} />
          {SCAN_STATES[scanStep]}
        </span>
      </div>

      {/* ── Photo + scan overlay ── */}
      <div style={{ position: 'relative', height: 420, overflow: 'hidden' }}>

        {/* Base photo */}
        <img
          src="https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=600&q=85"
          alt="Biometric facial scan"
          style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 55%', display: 'block' }}
        />

        {/* Dark tint overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to bottom, rgba(8,5,15,0.35) 0%, rgba(8,5,15,0.1) 40%, rgba(8,5,15,0.1) 70%, rgba(8,5,15,0.7) 100%)',
        }} />

        {/* Dot grid overlay */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'radial-gradient(circle, rgba(147,51,234,0.18) 1px, transparent 1px)',
          backgroundSize: '22px 22px',
          mixBlendMode: 'screen',
        }} />

        {/* Animated scan beam */}
        <div style={{
          position: 'absolute', left: 0, right: 0, height: 3,
          background: `linear-gradient(90deg, transparent 0%, ${accentColor2} 30%, ${accentColor} 50%, ${accentColor2} 70%, transparent 100%)`,
          boxShadow: `0 0 16px 4px ${accentColor}55`,
          animation: 'photoScanBeam 2.8s linear infinite',
          opacity: 0.85,
        }} />

        {/* Corner brackets */}
        {[
          { top: 12, left: 12,   borderTop: `2px solid ${accentColor}`, borderLeft:  `2px solid ${accentColor}` },
          { top: 12, right: 12,  borderTop: `2px solid ${accentColor}`, borderRight: `2px solid ${accentColor}` },
          { bottom: 12, left: 12,  borderBottom: `2px solid ${accentColor}`, borderLeft:  `2px solid ${accentColor}` },
          { bottom: 12, right: 12, borderBottom: `2px solid ${accentColor}`, borderRight: `2px solid ${accentColor}` },
        ].map((s, i) => (
          <div key={i} style={{ position: 'absolute', width: 24, height: 24, ...s, borderRadius: 2 }} />
        ))}

        {/* Facial landmark dots — approximate face region */}
        <svg style={{ position:'absolute', inset:0, width:'100%', height:'100%', pointerEvents:'none' }} viewBox="0 0 420 420" preserveAspectRatio="xMidYMid slice">
          <defs>
            <filter id="dotGlow">
              <feGaussianBlur stdDeviation="1.5" result="blur" />
              <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

{/* Landmark mesh lines */}
          <g stroke={accentColor} strokeWidth="0.5" opacity="0.2" fill="none">
            <line x1="225" y1="152" x2="178" y2="210" />
            <line x1="225" y1="152" x2="272" y2="210" />
            <line x1="178" y1="210" x2="272" y2="210" />
            <line x1="178" y1="210" x2="225" y2="257" />
            <line x1="272" y1="210" x2="225" y2="257" />
            <line x1="225" y1="257" x2="203" y2="290" />
            <line x1="225" y1="257" x2="247" y2="290" />
            <line x1="203" y1="290" x2="247" y2="290" />
            <line x1="203" y1="290" x2="225" y2="320" />
            <line x1="247" y1="290" x2="225" y2="320" />
            <line x1="137" y1="252" x2="178" y2="210" />
            <line x1="313" y1="252" x2="272" y2="210" />
          </g>

          {/* Landmark points */}
          <g fill={accentColor} filter="url(#dotGlow)">
            {[
              [225,152],[178,210],[272,210],[225,257],
              [203,290],[247,290],[225,320],
              [137,252],[313,252],[163,224],[287,224],
            ].map(([cx,cy],i) => (
              <circle key={i} cx={cx} cy={cy} r={3} opacity="0.9">
                <animate attributeName="opacity" values="0.4;1;0.4" dur="2s" repeatCount="indefinite" begin={`${i*0.12}s`} />
              </circle>
            ))}
          </g>

          {/* Eye circles */}
          <g fill="none" stroke={accentColor} strokeWidth="1.2" opacity="0.5">
            <circle cx="183" cy="214" r="15" />
            <circle cx="183" cy="214" r="5" fill={accentColor} opacity="0.35" />
            <circle cx="268" cy="210" r="15" />
            <circle cx="268" cy="210" r="5" fill={accentColor} opacity="0.35" />
          </g>

          {/* Verified check */}
          {isVerified && (
            <g>
              <circle cx="225" cy="252" r="32" fill="rgba(132,204,22,0.1)" stroke="#84CC16" strokeWidth="1.5" />
              <path d="M211,252 L222,263 L241,239" fill="none" stroke="#84CC16" strokeWidth="3"
                strokeLinecap="round" strokeLinejoin="round" filter="url(#dotGlow)" />
            </g>
          )}

          {/* Side measurement ticks */}
          <g stroke={accentColor} strokeWidth="0.6" opacity="0.25">
            {[165,195,225,255,285,315].map(y => (
              <g key={y}>
                <line x1="90" y1={y} x2="108" y2={y} />
                <line x1="312" y1={y} x2="330" y2={y} />
              </g>
            ))}
          </g>
        </svg>

      </div>

      {/* Keyframe for scan beam */}
      <style>{`
        @keyframes photoScanBeam {
          0%   { top:0%;   opacity:0; }
          5%   { opacity:0.85; }
          95%  { opacity:0.85; }
          100% { top:100%; opacity:0; }
        }
      `}</style>
    </div>
  );
};

const OrganizerPromo = () => (
  <section className="organizer-promo">
    {/* ambient glows */}
    <div style={{ position:'absolute', top:0, right:0, width:500, height:500,
      background:'radial-gradient(circle, rgba(147,51,234,0.05) 0%, transparent 70%)',
      borderRadius:'50%', filter:'blur(120px)', pointerEvents:'none' }} />
    <div style={{ position:'absolute', bottom:0, left:0, width:400, height:400,
      background:'radial-gradient(circle, rgba(236,72,153,0.04) 0%, transparent 70%)',
      borderRadius:'50%', filter:'blur(100px)', pointerEvents:'none' }} />

    <div className="container" style={{ position:'relative', zIndex:10 }}>
      <div className="promo-content">

        {/* Text */}
        <motion.div
          className="promo-text"
          initial={{ opacity:0, y:30 }}
          whileInView={{ opacity:1, y:0 }}
          viewport={{ once:true }}
          transition={{ duration:0.6 }}
        >
          <div className="promo-badge">
            <ScanFace size={16} />
            Facial Recognition Technology
          </div>

          <h2 className="section-title" style={{ textAlign:'left', marginBottom:24, fontSize:44 }}>
            Skip the Queue with{' '}
            <span className="highlight">Face&nbsp;ID&nbsp;Ticketing</span>
          </h2>

          <p className="promo-description">
            Experience seamless, ticketless entry for concerts, stadiums, and festivals.
            Our revolutionary biometric technology eliminates wait times, removes physical
            tickets, and delivers a premium experience for both organizers and attendees.
          </p>

          <div className="promo-buttons">
            <a href="https://play.google.com/store/apps/details?id=com.thrillathon.whooppeapp" target="_blank" rel="noopener noreferrer" className="btn-primary">
              <Brain size={18} />
              Download App
            </a>
            <Link to="/contact" className="btn-secondary">List Your Event</Link>
          </div>

          <div style={{ display:'flex', gap:28, marginTop:36, flexWrap:'wrap' }}>
            {[
              { icon:'🔒', text:'AES-256 Encrypted' },
              { icon:'🌐', text:'GDPR Compliant'    },
              { icon:'⚡', text:'0.3s Entry Speed'  },
            ].map(t => (
              <div key={t.text} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:'#9B8CC0' }}>
                <span>{t.icon}</span><span>{t.text}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Scanner */}
        <motion.div
          className="promo-visual"
          initial={{ opacity:0, x:40 }}
          whileInView={{ opacity:1, x:0 }}
          viewport={{ once:true }}
          transition={{ duration:0.7, delay:0.15 }}
          style={{ position:'relative', display:'flex', justifyContent:'center', alignItems:'center' }}
        >
          <FaceScanner />
        </motion.div>

      </div>
    </div>
  </section>
);

export default OrganizerPromo;
